import { useNavigate } from "react-router-dom";
import { MapPin, CalendarClock, Flame } from "lucide-react";

import { leadTypeLabel } from "@/config/crmOptions";
import { fmtDate, priceRange } from "@/lib/format";
import { Badge } from "@/components/ui/badge";

const tempClass = (t) => ({
  hot: "bg-red-100 text-red-700",
  warm: "bg-amber-100 text-amber-700",
  cold: "bg-sky-100 text-sky-700",
}[t] || "bg-secondary text-muted-foreground");

const prioClass = (p) => ({
  high: "border-red-300 text-red-700",
  normal: "border-border text-muted-foreground",
  low: "border-border text-muted-foreground",
}[p] || "border-border text-muted-foreground");

/** Compact lead summary card for the Kanban board / mobile list. */
export const LeadCard = ({ lead, dragging, footer }) => {
  const navigate = useNavigate();
  const contact = lead.contact || {};
  const range = priceRange(lead.price_min, lead.price_max);

  return (
    <div
      onClick={() => contact.id && navigate(`/crm/${contact.id}`)}
      data-testid={`lead-card-${lead.id}`}
      className={`cursor-pointer rounded-lg border bg-card p-3 shadow-sm transition-shadow hover:shadow-card ${dragging ? "border-brand-gold ring-2 ring-brand-gold/30" : "border-border"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-foreground">{contact.first_name} {contact.last_name}</p>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${tempClass(lead.temperature)}`}>{lead.temperature}</span>
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">{leadTypeLabel(lead.lead_type)}</p>
      {range && <p className="mt-1 text-xs font-medium text-foreground">{range}</p>}
      {lead.desired_location && (
        <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {lead.desired_location}</p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <Badge variant="outline" className={`text-[10px] ${prioClass(lead.priority)}`}>
          {lead.priority === "high" && <Flame className="mr-0.5 h-2.5 w-2.5" />}{lead.priority}
        </Badge>
        {lead.next_followup_at && (() => {
          const overdue = new Date(lead.next_followup_at) < new Date();
          return (
            <span className={`flex items-center gap-1 text-[10px] ${overdue ? "font-medium text-red-600" : "text-muted-foreground"}`} data-testid={`lead-followup-${lead.id}`}>
              <CalendarClock className="h-3 w-3" /> {overdue ? "Overdue " : ""}{fmtDate(lead.next_followup_at)}
            </span>
          );
        })()}
      </div>
      {footer}
    </div>
  );
};
